/**
 * Client-Side Email Service
 * 
 * This service provides client-side email functionality for the CRM application.
 * It handles email operations through Supabase Edge Functions and provides
 * a clean interface for frontend components.
 * 
 * @author CRM Development Team
 * @version 1.0.0
 */

import { supabase } from './supabase';

// Types
export interface EmailRequest {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  templateId?: string;
  dynamicTemplateData?: Record<string, any>;
  emailType?: string;
  companyId?: string;
  userId?: string;
  clientId?: string;
  categories?: string[];
  customArgs?: Record<string, any>;
  attachments?: Array<{
    content: string;
    type: string;
    filename: string;
    disposition?: string;
  }>;
}

export interface EmailResponse {
  success: boolean;
  messageId?: string;
  error?: string;
  details?: string[];
}

export interface EmailTemplate {
  name: string;
  type: string;
  subject: string;
  text: string;
  html: string;
  defaultData?: Record<string, any>;
}

/**
 * Client Email Service Class
 * 
 * Provides methods for sending emails from the client-side application.
 * All email operations are routed through Supabase Edge Functions for security.
 */
export class ClientEmailService {
  private static instance: ClientEmailService;

  constructor() {
    // Singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ClientEmailService {
    if (!ClientEmailService.instance) {
      ClientEmailService.instance = new ClientEmailService();
    }
    return ClientEmailService.instance;
  }

  /**
   * Send email through Supabase Edge Function
   * 
   * @param emailData - Email data to send
   * @returns Promise<EmailResponse>
   */
  public async sendEmail(emailData: EmailRequest): Promise<EmailResponse> {
    try {
      // Get the current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      // Check email consent if sending to a user (userId provided in emailData)
      if (emailData.userId) {
        try {
          const { hasEmailConsent } = await import('./notificationConsent');
          const hasConsent = await hasEmailConsent(emailData.userId);
          if (!hasConsent) {
            console.warn(`Email not sent - user ${emailData.userId} has not consented to email`);
            return {
              success: false,
              error: 'User has not consented to email notifications',
              noConsent: true
            };
          }
        } catch (consentError) {
          console.warn('Failed to check email consent:', consentError);
          // Continue with sending if consent check fails
        }
      }
      
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: emailData,
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (error) {
        console.error('Email sending error:', error);
        return {
          success: false,
          error: error.message || 'Failed to send email'
        };
      }

      return data as EmailResponse;
    } catch (error) {
      console.error('Email service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Send welcome email to new user
   */
  public async sendWelcomeEmail(
    recipientEmail: string,
    userName: string,
    companyName: string,
    companyId: string,
    userId: string
  ): Promise<EmailResponse> {
    const emailData: EmailRequest = {
      to: recipientEmail,
      subject: `Welcome to ${companyName}!`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Welcome to ${companyName}!</h2>
          <p>Hello ${userName},</p>
          <p>Your account has been created successfully. You can now access your CRM dashboard.</p>
          <p>Login URL: <a href="${window.location.origin}">${window.location.origin}</a></p>
          <p>Best regards,<br>The ${companyName} Team</p>
        </div>
      `,
      text: `Welcome ${userName}! Your account has been created successfully. You can now access your CRM dashboard at ${window.location.origin}`,
      emailType: 'welcome',
      companyId,
      userId,
      categories: ['welcome', 'user-management'],
      customArgs: { source: 'system' }
    };

    return await this.sendEmail(emailData);
  }

  /**
   * Send estimate email to client
   */
  public async sendEstimateEmail(
    clientEmail: string,
    clientName: string,
    estimateData: {
      estimateNumber: string;
      totalAmount: number;
      validUntil: string;
      companyName: string;
    },
    companyId: string,
    clientId: string
  ): Promise<EmailResponse> {
    const emailData: EmailRequest = {
      to: clientEmail,
      subject: `New Estimate #${estimateData.estimateNumber} - ${clientName}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>New Estimate #${estimateData.estimateNumber}</h2>
          <p>Hello ${clientName},</p>
          <p>A new estimate has been created for your project.</p>
          <div style="background: #f5f5f5; padding: 20px; margin: 20px 0;">
            <h3>Estimate Details</h3>
            <p><strong>Estimate Number:</strong> ${estimateData.estimateNumber}</p>
            <p><strong>Total Amount:</strong> $${estimateData.totalAmount.toFixed(2)}</p>
            <p><strong>Valid Until:</strong> ${estimateData.validUntil}</p>
          </div>
          <p>Please review the estimate and let us know if you have any questions.</p>
          <p>Best regards,<br>${estimateData.companyName}</p>
        </div>
      `,
      text: `New Estimate #${estimateData.estimateNumber} for ${clientName}. Total: $${estimateData.totalAmount.toFixed(2)}. Valid until: ${estimateData.validUntil}`,
      emailType: 'estimate',
      companyId,
      clientId,
      categories: ['estimate', 'client-communication'],
      customArgs: { source: 'automation' }
    };

    return await this.sendEmail(emailData);
  }

  /**
   * Send invoice email to client
   */
  public async sendInvoiceEmail(
    clientEmail: string,
    clientName: string,
    invoiceData: {
      invoiceNumber: string;
      totalAmount: number;
      dueDate: string;
      companyName: string;
    },
    companyId: string,
    clientId: string
  ): Promise<EmailResponse> {
    const emailData: EmailRequest = {
      to: clientEmail,
      subject: `Your Invoice from ${invoiceData.companyName} - #${invoiceData.invoiceNumber}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center;">
                <h1 style="margin: 0; font-size: 28px; font-weight: bold;">Your Invoice is Ready!</h1>
                <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">${invoiceData.companyName}</p>
            </div>
            
            <div style="background: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
                <p style="font-size: 16px; color: #333; margin-bottom: 20px;">Dear ${clientName},</p>
                
                <p style="font-size: 16px; color: #333; margin-bottom: 20px;">
                    Thank you for your business! We've prepared your invoice for the services provided.
                </p>
                
                <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                    <h3 style="color: #333; margin-top: 0;">Invoice Summary:</h3>
                    <p style="margin: 5px 0; color: #666;"><strong>Invoice #:</strong> ${invoiceData.invoiceNumber}</p>
                    <p style="margin: 5px 0; color: #666;"><strong>Total Amount:</strong> $${invoiceData.totalAmount.toFixed(2)}</p>
                    ${invoiceData.dueDate ? `<p style="margin: 5px 0; color: #666;"><strong>Due Date:</strong> ${invoiceData.dueDate}</p>` : ''}
                </div>
                
                <p style="font-size: 14px; color: #666; margin-top: 30px;">
                    If you have any questions about this invoice, please don't hesitate to contact us.
                </p>
                
                <p style="font-size: 14px; color: #666; margin-top: 20px;">
                    Best regards,<br>
                    ${invoiceData.companyName}
                </p>
            </div>
        </div>
      `,
      text: `Dear ${clientName},
      
Thank you for your business! We've prepared your invoice for the services provided.

Invoice Summary:
- Invoice #: ${invoiceData.invoiceNumber}
- Total Amount: $${invoiceData.totalAmount.toFixed(2)}
${invoiceData.dueDate ? `- Due Date: ${invoiceData.dueDate}` : ''}

If you have any questions about this invoice, please contact us.

Best regards,
${invoiceData.companyName}`,
      emailType: 'invoice',
      companyId,
      clientId,
      categories: ['invoice', 'client-communication'],
      customArgs: { source: 'automation' }
    };

    return await this.sendEmail(emailData);
  }

  /**
   * Send job status update email
   */
  public async sendJobStatusUpdateEmail(
    clientEmail: string,
    clientName: string,
    jobData: {
      jobTitle: string;
      status: string;
      updatedAt: string;
      notes?: string;
      companyName: string;
    },
    companyId: string,
    clientId: string
  ): Promise<EmailResponse> {
    const emailData: EmailRequest = {
      to: clientEmail,
      subject: `Job Update: ${jobData.jobTitle} - ${jobData.status}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Job Status Update</h2>
          <p>Hello ${clientName},</p>
          <p>We have an update on your job: <strong>${jobData.jobTitle}</strong></p>
          <div style="background: #f5f5f5; padding: 20px; margin: 20px 0;">
            <h3>Job Details</h3>
            <p><strong>Job Title:</strong> ${jobData.jobTitle}</p>
            <p><strong>Status:</strong> ${jobData.status}</p>
            <p><strong>Updated:</strong> ${jobData.updatedAt}</p>
            ${jobData.notes ? `<p><strong>Notes:</strong> ${jobData.notes}</p>` : ''}
          </div>
          <p>If you have any questions, please don't hesitate to contact us.</p>
          <p>Best regards,<br>${jobData.companyName}</p>
        </div>
      `,
      text: `Job Update: ${jobData.jobTitle} - Status: ${jobData.status}. Updated: ${jobData.updatedAt}${jobData.notes ? `. Notes: ${jobData.notes}` : ''}`,
      emailType: 'job-update',
      companyId,
      clientId,
      categories: ['job-update', 'client-communication'],
      customArgs: { source: 'automation' }
    };

    return await this.sendEmail(emailData);
  }

  /**
   * Send payment reminder email
   */
  public async sendPaymentReminderEmail(
    clientEmail: string,
    clientName: string,
    paymentData: {
      invoiceNumber: string;
      amountDue: number;
      dueDate: string;
      daysPastDue: number;
      companyName: string;
    },
    companyId: string,
    clientId: string
  ): Promise<EmailResponse> {
    const emailData: EmailRequest = {
      to: clientEmail,
      subject: `Payment Reminder - Invoice #${paymentData.invoiceNumber}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Payment Reminder</h2>
          <p>Hello ${clientName},</p>
          <p>This is a friendly reminder that payment for the following invoice is due:</p>
          <div style="background: #f5f5f5; padding: 20px; margin: 20px 0;">
            <h3>Invoice Details</h3>
            <p><strong>Invoice Number:</strong> ${paymentData.invoiceNumber}</p>
            <p><strong>Amount Due:</strong> $${paymentData.amountDue.toFixed(2)}</p>
            <p><strong>Due Date:</strong> ${paymentData.dueDate}</p>
            <p><strong>Days Past Due:</strong> ${paymentData.daysPastDue}</p>
          </div>
          <p>Please process payment at your earliest convenience to avoid any late fees.</p>
          <p>Best regards,<br>${paymentData.companyName}</p>
        </div>
      `,
      text: `Payment Reminder - Invoice #${paymentData.invoiceNumber}. Amount Due: $${paymentData.amountDue.toFixed(2)}. Due: ${paymentData.dueDate}. Days Past Due: ${paymentData.daysPastDue}`,
      emailType: 'payment-reminder',
      companyId,
      clientId,
      categories: ['payment-reminder', 'client-communication'],
      customArgs: { source: 'automation' }
    };

    return await this.sendEmail(emailData);
  }

  /**
   * Send custom email
   */
  public async sendCustomEmail(
    to: string,
    subject: string,
    content: {
      text?: string;
      html?: string;
    },
    options: {
      emailType?: string;
      companyId?: string;
      userId?: string;
      clientId?: string;
      categories?: string[];
      customArgs?: Record<string, any>;
    } = {}
  ): Promise<EmailResponse> {
    const emailData: EmailRequest = {
      to,
      subject,
      text: content.text,
      html: content.html,
      emailType: options.emailType || 'general',
      companyId: options.companyId,
      userId: options.userId,
      clientId: options.clientId,
      categories: options.categories || ['custom'],
      customArgs: { source: 'manual', ...options.customArgs }
    };

    return await this.sendEmail(emailData);
  }

  /**
   * Test email configuration
   */
  public async testEmailConfiguration(): Promise<EmailResponse> {
    const testEmail = (import.meta as any).env.VITE_TEST_EMAIL;
    if (!testEmail) {
      throw new Error('VITE_TEST_EMAIL environment variable is required for testing');
    }
    
    return await this.sendCustomEmail(
      testEmail,
      'SendGrid Configuration Test',
      {
        text: 'This is a test email to verify SendGrid configuration.',
        html: '<p>This is a test email to verify SendGrid configuration.</p>'
      },
      {
        emailType: 'test',
        categories: ['test']
      }
    );
  }
}

// Export singleton instance
export const clientEmailService = ClientEmailService.getInstance();

// Export individual functions for convenience
export const sendEmail = (emailData: EmailRequest) => clientEmailService.sendEmail(emailData);
export const sendWelcomeEmail = (recipientEmail: string, userName: string, companyName: string, companyId: string, userId: string) => 
  clientEmailService.sendWelcomeEmail(recipientEmail, userName, companyName, companyId, userId);
export const sendEstimateEmail = (clientEmail: string, clientName: string, estimateData: any, companyId: string, clientId: string) => 
  clientEmailService.sendEstimateEmail(clientEmail, clientName, estimateData, companyId, clientId);
export const sendInvoiceEmail = (clientEmail: string, clientName: string, invoiceData: any, companyId: string, clientId: string) => 
  clientEmailService.sendInvoiceEmail(clientEmail, clientName, invoiceData, companyId, clientId);
export const sendJobStatusUpdateEmail = (clientEmail: string, clientName: string, jobData: any, companyId: string, clientId: string) => 
  clientEmailService.sendJobStatusUpdateEmail(clientEmail, clientName, jobData, companyId, clientId);
export const sendPaymentReminderEmail = (clientEmail: string, clientName: string, paymentData: any, companyId: string, clientId: string) => 
  clientEmailService.sendPaymentReminderEmail(clientEmail, clientName, paymentData, companyId, clientId);
export const sendCustomEmail = (to: string, subject: string, content: any, options?: any) => 
  clientEmailService.sendCustomEmail(to, subject, content, options);
export const testEmailConfiguration = () => clientEmailService.testEmailConfiguration();